import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useAuth } from '../contexts/AuthContext';
import AuthLayout from '../components/AuthLayout';

const EyeIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const EyeOffIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M9.9 4.24A9.1 9.1 0 0 1 12 4c6.5 0 10 8 10 8a18 18 0 0 1-2.16 3.19M6.7 6.7A18 18 0 0 0 2 12s3.5 7 10 7a9 9 0 0 0 5.3-1.7" />
    <path d="M3 3l18 18" />
  </svg>
);

const Login = () => {
  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    formState: { errors }
  } = useForm();
  const {
    authMode,
    authConfigurationError,
    canUseLocalAuth,
    completeLocalAuth,
    completePasswordAuth,
    requestWhatsAppCode,
    verifyWhatsAppCode
  } = useAuth();
  const [step, setStep] = useState('identifier');
  const [identifier, setIdentifier] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (location.state?.email) {
      setValue('identifier', location.state.email);
    }
    if (location.state?.message) {
      setError(location.state.message);
    }
  }, [location.state, setValue]);

  const getErrorMessage = (err, fallback) => {
    if (err.response?.data?.error_message) return err.response.data.error_message;
    if (err.response?.data?.error) return err.response.data.error;
    return fallback;
  };

  const requestCode = async (data) => {
    try {
      setIsLoading(true);
      setError('');
      setSuccess('');

      const nextIdentifier = data.identifier.trim();
      setIdentifier(nextIdentifier);

      await requestWhatsAppCode(nextIdentifier);
      setStep('code');
      setSuccess('שלחנו קוד אימות לוואטסאפ שלך');
    } catch (err) {
      setError(getErrorMessage(err, 'שגיאה בשליחת קוד אימות. אנא נסה שנית.'));
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const verifyCode = async (data) => {
    try {
      setIsLoading(true);
      setError('');
      setSuccess('');

      await verifyWhatsAppCode(data.verificationCode, identifier || getValues('identifier'));
      setSuccess('התחברת בהצלחה');

      setTimeout(() => {
        navigate(location.state?.from || '/jobs');
      }, 500);
    } catch (err) {
      setError(getErrorMessage(err, 'שגיאה באימות קוד. אנא נסה שנית.'));
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const resendCode = async () => {
    try {
      setIsLoading(true);
      setError('');
      setSuccess('');

      await requestWhatsAppCode(identifier || getValues('identifier'));
      setSuccess('קוד חדש נשלח לוואטסאפ שלך');
    } catch (err) {
      setError(getErrorMessage(err, 'שגיאה בשליחת קוד חדש. אנא נסה שנית.'));
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const editIdentifier = () => {
    setStep('identifier');
    setSuccess('');
    setError('');
  };

  const handleDevLogin = async () => {
    try {
      setIsLoading(true);
      setError('');
      setSuccess('');

      await completeLocalAuth();
      navigate(location.state?.from || '/jobs');
    } catch (err) {
      setError('לא הצלחנו להיכנס במצב בדיקה. אנא נסי שוב.');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordLogin = async (data) => {
    try {
      setIsLoading(true);
      setError('');
      setSuccess('');

      await completePasswordAuth({
        email: data.testEmail.trim(),
        password: data.testPassword
      });
      navigate(location.state?.from || '/jobs');
    } catch (err) {
      setError(getErrorMessage(err, 'לא הצלחנו להיכנס עם משתמש הבדיקה. יש לבדוק את פרטי ההתחברות.'));
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthLayout
      title="התחברות"
      altText="אין לך חשבון?"
      altLinkText="הרשמה למערכת"
      altLinkTo="/register"
      LinkComponent={Link}
    >
      <p className="-mt-4 mb-6 text-center text-sm text-gray-500">
        {authMode === 'password'
          ? 'מצב בדיקה מול שרת production באמצעות משתמש קיים.'
          : 'הזן/י טלפון או אימייל ונשלח קוד אימות לוואטסאפ.'}
      </p>

      {error && (
        <div className="mb-6 rounded-xl border border-red-400 bg-red-100 px-4 py-3 text-red-700" role="alert">
          <span className="block sm:inline">{error}</span>
        </div>
      )}

      {success && (
        <div className="mb-6 rounded-xl border border-green-400 bg-green-100 px-4 py-3 text-green-700" role="status">
          <span className="block sm:inline">{success}</span>
        </div>
      )}

      {authConfigurationError && (
        <div className="mb-6 rounded-xl border border-red-400 bg-red-100 px-4 py-3 text-red-700" role="alert">
          תצורת ההזדהות אינה תקינה. יש לבדוק את משתני הסביבה של הפרונטאנד.
        </div>
      )}

      {authMode === 'password' ? (
        <form className="space-y-4" onSubmit={handleSubmit(handlePasswordLogin)}>
          <div>
            <label htmlFor="testEmail" className="auth-label">אימייל משתמש בדיקה</label>
            <input
              id="testEmail"
              type="email"
              {...register('testEmail', {
                required: 'אימייל הוא שדה חובה',
                pattern: {
                  value: /\S+@\S+\.\S+/,
                  message: 'אנא הזן/י כתובת אימייל תקינה'
                }
              })}
              className="auth-input"
              autoComplete="username"
              dir="ltr"
            />
            {errors.testEmail && (
              <p className="mt-1 text-xs text-red-500">{errors.testEmail.message}</p>
            )}
          </div>

          <div>
            <label htmlFor="testPassword" className="auth-label">סיסמה</label>
            <div className="relative">
              <input
                id="testPassword"
                type={showPassword ? 'text' : 'password'}
                {...register('testPassword', { required: 'סיסמה היא שדה חובה' })}
                className="auth-input pl-12"
                autoComplete="current-password"
                dir="ltr"
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                className="absolute inset-y-0 left-3 flex items-center text-gray-500 hover:text-gray-700"
                aria-label={showPassword ? 'הסתר סיסמה' : 'הצג סיסמה'}
              >
                {showPassword ? <EyeOffIcon /> : <EyeIcon />}
              </button>
            </div>
            {errors.testPassword && (
              <p className="mt-1 text-xs text-red-500">{errors.testPassword.message}</p>
            )}
          </div>

          <button type="submit" disabled={isLoading || Boolean(authConfigurationError)} className="btn-orange">
            {isLoading ? 'מתחבר...' : 'כניסה לבדיקה'}
          </button>
        </form>
      ) : step === 'identifier' ? (
        <form className="space-y-4" onSubmit={handleSubmit(requestCode)}>
          <div>
            <label htmlFor="identifier" className="auth-label">טלפון או אימייל</label>
            <input
              id="identifier"
              type="text"
              {...register('identifier', {
                required: 'טלפון או אימייל הם שדה חובה',
                validate: (value) => {
                  const trimmedValue = value.trim();
                  const isEmail = /\S+@\S+\.\S+/.test(trimmedValue);
                  const isPhone = /^[+]?[\d\s-]{8,}$/.test(trimmedValue);
                  return isEmail || isPhone || 'אנא הזן/י טלפון או אימייל תקינים';
                }
              })}
              className="auth-input"
              placeholder="example@mail.com או 0501234567"
              dir="ltr"
            />
            {errors.identifier && (
              <p className="text-red-500 text-xs mt-1">{errors.identifier.message}</p>
            )}
          </div>

          <button type="submit" disabled={isLoading} className="btn-orange">
            {isLoading ? 'שולח...' : 'שליחת קוד אימות'}
          </button>

          {canUseLocalAuth && (
            <button
              type="button"
              onClick={handleDevLogin}
              disabled={isLoading}
              className={`btn-secondary w-full ${isLoading ? 'opacity-70 cursor-not-allowed' : ''}`}
            >
              כניסה לבדיקה ללא קוד
            </button>
          )}
        </form>
      ) : (
        <form className="space-y-4" onSubmit={handleSubmit(verifyCode)}>
          <div className="rounded-xl border border-[#e0b877] bg-white/60 p-3 text-sm text-gray-700">
            הקוד נשלח עבור: <span dir="ltr" className="font-medium">{identifier}</span>
            <button
              type="button"
              onClick={editIdentifier}
              className="mr-3 text-blue-600 hover:text-blue-500"
            >
              שינוי
            </button>
          </div>

          <div>
            <label htmlFor="verificationCode" className="auth-label">קוד אימות</label>
            <input
              id="verificationCode"
              type="text"
              {...register('verificationCode', {
                required: 'קוד אימות הוא שדה חובה',
                pattern: {
                  value: /^\d+$/,
                  message: 'קוד אימות חייב להכיל רק ספרות'
                }
              })}
              className="auth-input text-center text-2xl tracking-widest"
              placeholder="_ _ _ _ _ _"
              dir="ltr"
              maxLength={6}
            />
            {errors.verificationCode && (
              <p className="text-red-500 text-xs mt-1">{errors.verificationCode.message}</p>
            )}
          </div>

          <div className="flex flex-col space-y-2">
            <button type="submit" disabled={isLoading} className="btn-orange">
              {isLoading ? 'מאמת...' : 'התחברות'}
            </button>
            <button
              type="button"
              onClick={resendCode}
              disabled={isLoading}
              className={`btn-secondary w-full ${isLoading ? 'opacity-70 cursor-not-allowed' : ''}`}
            >
              שליחת קוד חדש
            </button>
          </div>
        </form>
      )}
    </AuthLayout>
  );
};

export default Login;
